import {
  HEX_IDENTIFIER_PATTERN,
  HEX_WITH_OPAQUE_SUFFIX_PATTERN,
  NUMERIC_ID_PATTERN,
  SHORT_HEX_IDENTIFIER_PATTERN,
  UUID_PATTERN,
  decodePathSegment,
} from './segment-identifier-patterns';
import {
  isLocaleShapedSegment,
  segmentValueClass,
} from './segment-value-class';

export const PARAM_SLOT_CLASSIFIERS = [
  'numeric',
  'uuid',
  'hex',
  'locale',
  'opaque',
  'identifier',
] as const;

export type ParamSlotClassifier = (typeof PARAM_SLOT_CLASSIFIERS)[number];

export const NUMBERING_IDENTIFIER_CLASSES = [
  'numeric',
  'identifier',
] as const satisfies ReadonlyArray<ParamSlotClassifier>;

export type NumberingIdentifierClass =
  (typeof NUMBERING_IDENTIFIER_CLASSES)[number];

export const PARAM_SLOT_CLASSIFIER_ALTERNATION =
  PARAM_SLOT_CLASSIFIERS.join('|');

const PARAM_SLOT_CLASSIFIER_SET = new Set<string>(PARAM_SLOT_CLASSIFIERS);

const WHOLE_PARAM_SEGMENT_PATTERN =
  /^\{([a-zA-Z][a-zA-Z0-9_]*)(?::([a-z][a-z0-9_]*))?\}$/;

export type ParsedParamSlot = {
  readonly name: string;
  readonly classifier: ParamSlotClassifier | null;
};

export type ConcreteSegmentClassifierClass =
  | ParamSlotClassifier
  | 'route_word'
  | 'other';

export function paramSlotPlaceholderPattern(flags = ''): RegExp {
  return new RegExp(
    `\\{[a-z][a-z0-9_]*(?::(?:${PARAM_SLOT_CLASSIFIER_ALTERNATION}))?\\}`,
    flags,
  );
}

export function paramSlotNameCapturePattern(flags = ''): RegExp {
  return new RegExp(
    `\\{([a-z][a-z0-9_]*)(?::(?:${PARAM_SLOT_CLASSIFIER_ALTERNATION}))?\\}`,
    flags,
  );
}

function classifyConcreteSegment(
  segment: string,
): ConcreteSegmentClassifierClass {
  const decoded = decodePathSegment(segment);

  if (!decoded) {
    return 'other';
  }

  if (NUMERIC_ID_PATTERN.test(decoded)) {
    return 'numeric';
  }

  if (UUID_PATTERN.test(decoded)) {
    return 'uuid';
  }

  if (
    HEX_IDENTIFIER_PATTERN.test(decoded) ||
    HEX_WITH_OPAQUE_SUFFIX_PATTERN.test(decoded)
  ) {
    return 'hex';
  }

  if (SHORT_HEX_IDENTIFIER_PATTERN.test(decoded)) {
    return 'opaque';
  }

  if (isLocaleShapedSegment(decoded)) {
    return 'locale';
  }

  const valueClass = segmentValueClass(decoded);

  if (valueClass === 'identifier' || valueClass === 'colon_delimited') {
    return 'opaque';
  }

  if (valueClass === 'route_word') {
    return 'route_word';
  }

  return 'other';
}

export function isParamSlotClassifier(
  value: string,
): value is ParamSlotClassifier {
  return PARAM_SLOT_CLASSIFIER_SET.has(value);
}

export function isNumberingIdentifierClass(
  value: string,
): value is NumberingIdentifierClass {
  return (NUMBERING_IDENTIFIER_CLASSES as readonly string[]).includes(value);
}

export function parseParamSlot(segment: string): ParsedParamSlot | null {
  const match = WHOLE_PARAM_SEGMENT_PATTERN.exec(segment);

  if (!match) {
    return null;
  }

  const name = match[1];
  const rawClassifier = match[2];

  if (!rawClassifier) {
    return { name, classifier: null };
  }

  if (!isParamSlotClassifier(rawClassifier)) {
    return null;
  }

  return { name, classifier: rawClassifier };
}

export function formatParamSlot({
  name,
  classifier,
}: {
  name: string;
  classifier: ParamSlotClassifier | null;
}): string {
  return classifier ? `{${name}:${classifier}}` : `{${name}}`;
}

export function listParamSlotClassifiers(
  pattern: string,
): ReadonlyArray<{ name: string; classifier: ParamSlotClassifier }> {
  return pattern
    .split('/')
    .filter(Boolean)
    .flatMap((segment) => {
      const parsed = parseParamSlot(segment);

      if (!parsed?.classifier) {
        return [];
      }

      return [{ name: parsed.name, classifier: parsed.classifier }];
    });
}

export function stripParamSlotSuffixes(pattern: string): string {
  const segments = pattern.split('/').filter(Boolean);
  const strippedSegments = segments.map((segment) => {
    const match = WHOLE_PARAM_SEGMENT_PATTERN.exec(segment);

    if (!match?.[1]) {
      return segment;
    }

    return formatParamSlot({ name: match[1], classifier: null });
  });

  return `/${strippedSegments.join('/')}`;
}

export function stripParamSlotClassifiers(pattern: string): string {
  const segments = pattern.split('/').filter(Boolean);
  const strippedSegments = segments.map((segment) => {
    const parsed = parseParamSlot(segment);

    if (!parsed?.classifier) {
      return segment;
    }

    return formatParamSlot({ name: parsed.name, classifier: null });
  });

  return `/${strippedSegments.join('/')}`;
}

export function concreteSegmentMatchesParamSlot({
  concreteSegment,
  patternSegment,
}: {
  concreteSegment: string;
  patternSegment: string;
}): boolean | null {
  const parsed = parseParamSlot(patternSegment);

  if (!parsed) {
    return null;
  }

  if (parsed.classifier === null || parseParamSlot(concreteSegment)) {
    return true;
  }

  return isConcreteCompatibleWithClassifier({
    concreteSegment,
    classifier: parsed.classifier,
  });
}

export function isConcreteCompatibleWithClassifier({
  concreteSegment,
  classifier,
}: {
  concreteSegment: string;
  classifier: ParamSlotClassifier;
}): boolean {
  const concreteClass = classifyConcreteSegment(concreteSegment);

  if (concreteClass === 'route_word' || concreteClass === 'other') {
    return false;
  }

  switch (classifier) {
    case 'numeric':
      return concreteClass === 'numeric';
    case 'uuid':
      return concreteClass === 'uuid';
    case 'hex':
      return concreteClass === 'hex';
    case 'locale':
      return concreteClass === 'locale';
    case 'opaque':
      return concreteClass === 'opaque' || concreteClass === 'hex';
    case 'identifier':
      return (
        concreteClass === 'numeric' ||
        concreteClass === 'uuid' ||
        concreteClass === 'hex' ||
        concreteClass === 'opaque'
      );
  }
}

export function resolveCommonParamSlotClassifier(
  values: readonly string[],
): ParamSlotClassifier | null {
  if (values.length === 0) {
    return null;
  }

  const classes = values.map((value) => classifyConcreteSegment(value));
  const identifierClasses = classes.filter(
    (valueClass): valueClass is ParamSlotClassifier =>
      valueClass !== 'route_word' && valueClass !== 'other',
  );

  if (identifierClasses.length !== classes.length) {
    return null;
  }

  const unique = new Set(identifierClasses);

  if (unique.size === 1) {
    const [only] = unique;

    return only ?? null;
  }

  if (
    [...unique].every(
      (valueClass) => valueClass === 'hex' || valueClass === 'opaque',
    )
  ) {
    return 'opaque';
  }

  return 'identifier';
}

export function resolveMajorityParamSlotClassifier(
  values: readonly string[],
): ParamSlotClassifier | null {
  const counts = new Map<ParamSlotClassifier, number>();

  for (const value of values) {
    if (parseParamSlot(value)) {
      continue;
    }

    const valueClass = classifyConcreteSegment(value);

    if (!isParamSlotClassifier(valueClass)) {
      continue;
    }

    counts.set(valueClass, (counts.get(valueClass) ?? 0) + 1);
  }

  const ranked = [...counts.entries()].sort(
    (left, right) => right[1] - left[1],
  );
  const winner = ranked[0];

  if (!winner) {
    return null;
  }

  const [winnerClass, winnerCount] = winner;
  const runnerUpCount = ranked[1]?.[1] ?? 0;

  if (winnerCount <= runnerUpCount) {
    return null;
  }

  return winnerClass;
}
